import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { FaChevronDown, FaChevronLeft } from 'react-icons/fa';
import { ReactComponent as RoadmapNodeIcon } from '../../Icons/roadmap-node-icon.svg';
import { ReactComponent as NavBackIcon } from '../../Icons/nav-back.svg';
import { ReactComponent as NavForwardIcon } from '../../Icons/nav-forward.svg';
import IconRenderer from '../ui/IconRenderer';
import './StudyRoadmapContextPanel.css';

function StudyRoadmapContextDropdown({
  fieldLabel,
  value,
  displayLabel,
  options = [],
  disabled = false,
  loading = false,
  chipClassName = '',
  onSelect,
  onLockedSelect
}) {
  const listId = useId();
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);

  const selectedOption = options.find((option) => option.value === value);
  const triggerLabel = displayLabel || selectedOption?.label || '';
  const canOpen =
    !disabled &&
    !loading &&
    options.length > 1 &&
    typeof onSelect === 'function';

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [value]);

  const handleSelect = (nextValue) => {
    const option = options.find((item) => item.value === nextValue);
    if (option?.locked) {
      onLockedSelect?.(nextValue);
      setOpen(false);
      return;
    }
    if (nextValue === value) {
      setOpen(false);
      return;
    }
    onSelect?.(nextValue);
    setOpen(false);
  };

  return (
    <div className="study-roadmap-context__field">
      <span className="study-roadmap-context__field-label">{fieldLabel}</span>
      <div
        ref={containerRef}
        className={[
          'study-roadmap-context__dropdown',
          open && 'study-roadmap-context__dropdown--open',
          !canOpen && 'study-roadmap-context__dropdown--static'
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <button
          type="button"
          className={[
            'study-roadmap-context__chip',
            'study-roadmap-context__dropdown-trigger',
            chipClassName
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => canOpen && setOpen((prev) => !prev)}
          disabled={!canOpen}
          aria-haspopup="listbox"
          aria-expanded={canOpen ? open : false}
          aria-controls={canOpen ? listId : undefined}
          title={triggerLabel}
        >
          <span className="study-roadmap-context__chip-text">
            {loading ? 'Loading…' : triggerLabel}
          </span>
          {canOpen && (
            <FaChevronDown
              className="study-roadmap-context__dropdown-chevron"
              aria-hidden
            />
          )}
        </button>

        {open && canOpen && (
          <ul
            id={listId}
            className="study-roadmap-context__dropdown-menu"
            role="listbox"
            aria-label={fieldLabel}
          >
            {options.map((option) => (
              <li key={option.value} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={!option.locked && value === option.value}
                  aria-disabled={option.locked || undefined}
                  className={[
                    'study-roadmap-context__dropdown-option',
                    !option.locked &&
                      value === option.value &&
                      'study-roadmap-context__dropdown-option--selected',
                    option.locked && 'study-roadmap-context__dropdown-option--locked'
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handleSelect(option.value)}
                  title={
                    option.locked
                      ? `${option.label} — Subscribe to unlock`
                      : option.label
                  }
                >
                  <span className="study-roadmap-context__dropdown-option-text">
                    {option.label}
                  </span>
                  {option.locked && (
                    <span className="study-roadmap-context__dropdown-option-locked-label">
                      Pro
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Roadmap context while studying: a single horizontal bar above the study
 * card (breadcrumb + track progress). Click to open a compact switcher popover.
 */
export default function StudyRoadmapContextPanel({
  languageKey,
  languageLabel,
  trackTitle,
  topicTitle,
  trackOptions = [],
  currentTrackId = null,
  onTrackSelect,
  onTrackLockedSelect,
  topicOptions = [],
  currentTopicId = null,
  onTopicSelect,
  tracksLoading = false,
  topicsLoading = false,
  trackProgressCompleted = null,
  trackProgressTotal = null,
  loading = false,
  onViewTrack,
  showNodeNav = false,
  nodeNavTitle = '',
  canNavigatePrev = false,
  canNavigateNext = false,
  nodeNavLoading = false,
  onNavigatePrev,
  onNavigateNext
}) {
  const [expanded, setExpanded] = useState(false);
  const [scrimHost, setScrimHost] = useState(null);
  const [scrimInset, setScrimInset] = useState(null);
  const rootRef = useRef(null);

  const hasLanguage = Boolean(languageLabel);
  const hasTrack = Boolean(trackTitle);
  const hasTopic = Boolean(topicTitle);
  const hasTrackProgress =
    typeof trackProgressCompleted === 'number' &&
    typeof trackProgressTotal === 'number' &&
    trackProgressTotal > 0;

  const breadcrumbParts = [languageLabel, trackTitle, topicTitle].filter(Boolean);
  const breadcrumbText = breadcrumbParts.join(' › ');

  const collapse = useCallback(() => setExpanded(false), []);
  const toggleExpanded = useCallback(() => setExpanded((v) => !v), []);

  useEffect(() => {
    if (!expanded) {
      setScrimHost(null);
      setScrimInset(null);
      return undefined;
    }

    const updateScrim = () => {
      const root = rootRef.current;
      const layout = root?.closest('.study-viewer__layout');
      if (!root || !layout) {
        setScrimHost(null);
        setScrimInset(null);
        return;
      }

      const layoutRect = layout.getBoundingClientRect();
      const rootRect = root.getBoundingClientRect();
      setScrimHost(layout);
      setScrimInset({
        top: Math.max(0, rootRect.bottom - layoutRect.top),
        left: 0,
        right: 0,
        bottom: 0
      });
    };

    updateScrim();
    window.addEventListener('resize', updateScrim);
    return () => window.removeEventListener('resize', updateScrim);
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return undefined;
    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setExpanded(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [expanded]);

  if (!loading && !hasLanguage && !hasTrack && !hasTopic) return null;

  return (
    <div
      ref={rootRef}
      className={[
        'study-roadmap-context',
        expanded && 'study-roadmap-context--expanded'
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className="study-roadmap-context__bar"
        onClick={toggleExpanded}
        aria-expanded={expanded}
        title={expanded ? 'Close context switcher' : 'Open context switcher'}
      >
        <span className="study-roadmap-context__bar-icon-wrap" aria-hidden>
          <RoadmapNodeIcon className="study-roadmap-context__bar-icon" focusable="false" />
        </span>
        <span className="study-roadmap-context__bar-breadcrumb" title={breadcrumbText}>
          {breadcrumbText || 'Roadmap'}
        </span>
        {hasTrackProgress && (
          <>
            <span className="study-roadmap-context__bar-separator" aria-hidden>
              ·
            </span>
            <span
              className="study-roadmap-context__bar-progress"
              aria-label={`${trackProgressCompleted} of ${trackProgressTotal} topics completed in this track`}
            >
              {trackProgressCompleted}/{trackProgressTotal}
            </span>
          </>
        )}
        <FaChevronDown
          className="study-roadmap-context__bar-chevron"
          aria-hidden
        />
      </button>

      {expanded && scrimHost && scrimInset && createPortal(
        <button
          type="button"
          className="study-roadmap-context__scrim"
          style={scrimInset}
          onClick={collapse}
          aria-label="Close context switcher"
          tabIndex={-1}
        />,
        scrimHost
      )}

      {expanded && (
        <div
          className="study-roadmap-context__drawer"
          role="dialog"
          aria-label="Switch study context"
        >
          <div className="study-roadmap-context__drawer-header">
            <span className="study-roadmap-context__drawer-title">Switch context</span>
            <button
              type="button"
              className="study-roadmap-context__drawer-collapse"
              onClick={collapse}
              aria-label="Collapse roadmap context"
            >
              <FaChevronLeft aria-hidden />
            </button>
          </div>

          <div className="study-roadmap-context__drawer-body">
            {loading ? (
              <div className="study-roadmap-context__loading" aria-live="polite">
                <span className="study-roadmap-context__loading-dot" aria-hidden />
                <span className="study-roadmap-context__loading-dot" aria-hidden />
                <span className="study-roadmap-context__loading-dot" aria-hidden />
              </div>
            ) : (
              <>
                {hasLanguage && (
                  <div className="study-roadmap-context__field">
                    <span className="study-roadmap-context__field-label">Language</span>
                    <span className="study-roadmap-context__chip study-roadmap-context__chip--language">
                      {languageKey && (
                        <IconRenderer
                          name={languageKey}
                          type="language"
                          size="small"
                          className="study-roadmap-context__chip-icon"
                        />
                      )}
                      <span className="study-roadmap-context__chip-text">{languageLabel}</span>
                    </span>
                  </div>
                )}

                {hasTrack && (
                  <StudyRoadmapContextDropdown
                    fieldLabel="Track"
                    value={currentTrackId}
                    displayLabel={trackTitle}
                    options={trackOptions}
                    loading={tracksLoading}
                    chipClassName="study-roadmap-context__chip--track"
                    onSelect={(trackId) => {
                      onTrackSelect?.(trackId);
                      collapse();
                    }}
                    onLockedSelect={() => {
                      onTrackLockedSelect?.();
                      collapse();
                    }}
                  />
                )}

                {hasTopic && (
                  <StudyRoadmapContextDropdown
                    fieldLabel="Topic"
                    value={currentTopicId}
                    displayLabel={topicTitle}
                    options={topicOptions}
                    loading={topicsLoading}
                    chipClassName="study-roadmap-context__chip--topic"
                    onSelect={(topicId) => {
                      onTopicSelect?.(topicId);
                      collapse();
                    }}
                  />
                )}

              </>
            )}
          </div>

          {!loading && typeof onViewTrack === 'function' && (
            <div className="study-roadmap-context__drawer-footer">
              {showNodeNav && nodeNavTitle && (
                <div
                  className="study-roadmap-context__node-nav"
                  aria-label="Navigate topics in this track"
                >
                  <button
                    type="button"
                    className="study-roadmap-context__node-nav-button study-roadmap-context__node-nav-button--prev"
                    onClick={() => {
                      onNavigatePrev?.();
                      collapse();
                    }}
                    disabled={!canNavigatePrev || nodeNavLoading || typeof onNavigatePrev !== 'function'}
                    aria-label="Previous topic in track"
                  >
                    <NavBackIcon
                      className="study-roadmap-context__node-nav-button-icon"
                      aria-hidden
                      focusable="false"
                    />
                  </button>
                  <span
                    className="study-roadmap-context__node-nav-title"
                    title={nodeNavTitle}
                  >
                    {nodeNavTitle}
                  </span>
                  <button
                    type="button"
                    className="study-roadmap-context__node-nav-button study-roadmap-context__node-nav-button--next"
                    onClick={() => {
                      onNavigateNext?.();
                      collapse();
                    }}
                    disabled={!canNavigateNext || nodeNavLoading || typeof onNavigateNext !== 'function'}
                    aria-label="Next topic in track"
                  >
                    <NavForwardIcon
                      className="study-roadmap-context__node-nav-button-icon"
                      aria-hidden
                      focusable="false"
                    />
                  </button>
                </div>
              )}

              <button
                type="button"
                className="study-roadmap-context__view-track"
                onClick={onViewTrack}
              >
                <span className="study-roadmap-context__view-track-text">View track</span>
                <NavForwardIcon
                  className="study-roadmap-context__view-track-icon"
                  aria-hidden
                  focusable="false"
                />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
